import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './EditMessagePopupMobile.module.css';
import { MessageType, EditMessageProps } from '../../types/message';

const EditMessagePopupMobile: React.FC<EditMessageProps> = ({ 
  message,
  onSave,
  onClose,
  isVisible
}) => {
  const [content, setContent] = useState<string>(message.content);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isAnimatedIn, setIsAnimatedIn] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  
  // Reset content when message changes
  useEffect(() => {
    setContent(message.content);
  }, [message.content]);
  
  // Animation effect when component mounts
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure DOM is ready for the animation
      const timer = setTimeout(() => {
        setIsAnimatedIn(true);
        // Focus the textarea after animation
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 300);
      }, 10);
      
      // Prevent body scrolling when popup is open
      document.body.style.overflow = 'hidden';
      
      // Add a global touch capture handler to prevent any other touch events
      const captureAllTouches = (e: TouchEvent) => {
        if (backdropRef.current && !backdropRef.current.contains(e.target as Node)) {
          e.stopPropagation();
        }
      };
      
      document.addEventListener('touchstart', captureAllTouches, true);
      document.addEventListener('touchmove', captureAllTouches, true);
      document.addEventListener('touchend', captureAllTouches, true);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('touchstart', captureAllTouches, true);
        document.removeEventListener('touchmove', captureAllTouches, true);
        document.removeEventListener('touchend', captureAllTouches, true);
      };
    }
  }, [isVisible]);
  
  // Handle close with animation
  const handleClose = (): void => {
    setIsAnimatedIn(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      document.body.style.overflow = '';
      onClose();
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (content.trim() === message.content) {
      handleClose();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(content.trim());
      handleClose();
    } catch (err) {
      console.error('Failed to save message:', err);
      // Could add error state handling here
    } finally {
      setIsSaving(false);
    }
  };

  // Specific handler to prevent touch events from propagating
  const preventTouchPropagation = (e: React.TouchEvent): void => {
    e.stopPropagation();
  };

  if (!isVisible) return null;

  return createPortal(
    <div 
      ref={backdropRef}
      className={`${styles.backdrop} ${isAnimatedIn ? styles.backdropVisible : ''}`}
      onClick={handleClose}
      onTouchStart={preventTouchPropagation}
      onTouchMove={preventTouchPropagation}
      onTouchEnd={preventTouchPropagation}
    >
      <div 
        className={`${styles.popupContainer} ${isAnimatedIn ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.handle}></div>
          <h3 className={styles.title}>Edit Message</h3>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className={styles.textarea}
            disabled={isSaving}
            placeholder="Edit your message..."
          />
          
          <div className={styles.buttons}>
            <button 
              type="button" 
              onClick={handleClose} 
              className={styles.cancelButton}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={`${styles.saveButton} ${isSaving ? styles.saving : ''}`}
              disabled={content.trim() === message.content || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditMessagePopupMobile; 